// POS → Restaurant → Fotos del menú (/pos/rest-fotos).
// Sube o cambia la foto de cada plato (producto vendible). Reusa la tabla
// satélite inventario.producto_imagenes (la misma del POS/Inventario): la foto
// aparece en la carta QR del cliente y en el buscador del Salón.
import { useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ImageOff, Search, Trash2, Upload, UtensilsCrossed } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/Card';
import { apiClient, getErrorMessage } from '@/lib/api/client';
import { deleteProductoImagen, resizeToDataURL, upsertProductoImagen } from '@/features/inventario/shared';
import { fieldCls, money, productoThumbs } from './shared';

interface PlatoT { id: string; codigo: string | null; nombre: string; precio_base: number }

export function FotosMenu() {
  const qc = useQueryClient();
  const [q, setQ] = useState('');
  const [subiendo, setSubiendo] = useState<string | null>(null);
  const fileRefs = useRef<Record<string, HTMLInputElement | null>>({});

  // Platos del menú = productos vendibles (excluye internos REST-*)
  const { data: platos = [] } = useQuery<PlatoT[]>({
    queryKey: ['rest-fotos-platos'],
    queryFn: async () => (await apiClient.get<PlatoT[]>(
      '/rest/inventario.productos?select=id,codigo,nombre,precio_base' +
      '&precio_base=gt.0&es_activo=is.true&deleted_at=is.null' +
      '&codigo=not.in.(REST-SERVICIO,REST-CONSUMO)&order=nombre')).data,
  });
  const ids = useMemo(() => platos.map((p) => p.id), [platos]);
  const { data: thumbs = {} } = useQuery<Record<string, string>>({
    queryKey: ['rest-fotos-thumbs', ids.join(',')],
    queryFn: () => productoThumbs(ids),
    enabled: ids.length > 0, staleTime: 0,
  });

  const filtrados = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return platos;
    return platos.filter((p) => p.nombre.toLowerCase().includes(t) || (p.codigo ?? '').toLowerCase().includes(t));
  }, [platos, q]);

  const refrescar = () => {
    void qc.invalidateQueries({ queryKey: ['rest-fotos-thumbs'] });
    void qc.invalidateQueries({ queryKey: ['pos-prod-thumbs'] }); // el POS/F1 reusa thumbs
    void qc.invalidateQueries({ queryKey: ['qr-carta'] });        // la carta QR se refresca
  };

  const subir = async (plato: PlatoT, file: File | undefined) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Selecciona un archivo de imagen'); return; }
    setSubiendo(plato.id);
    try {
      const imagen = await resizeToDataURL(file, 800, 0.75);
      const thumb = await resizeToDataURL(file, 160, 0.6);
      await upsertProductoImagen(plato.id, { mime: 'image/jpeg', imagen, thumb });
      refrescar();
      toast.success(`Foto de «${plato.nombre}» actualizada`);
    } catch (e) { toast.error(getErrorMessage(e)); }
    finally { setSubiendo(null); }
  };

  const quitar = async (plato: PlatoT) => {
    setSubiendo(plato.id);
    try {
      await deleteProductoImagen(plato.id);
      refrescar();
      toast.success(`Foto de «${plato.nombre}» quitada`);
    } catch (e) { toast.error(getErrorMessage(e)); }
    finally { setSubiendo(null); }
  };

  const conFoto = platos.filter((p) => thumbs[p.id]).length;

  return (
    <div className="p-6 space-y-4 max-w-6xl">
      <div className="flex items-center gap-3 flex-wrap">
        <h1 className="text-xl font-semibold flex items-center gap-2"><UtensilsCrossed className="w-5 h-5" /> Fotos del menú</h1>
        <span className="text-sm text-muted-foreground">{conFoto}/{platos.length} platos con foto</span>
        <div className="relative ml-auto">
          <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input className={`${fieldCls} !w-64 !pl-8`} placeholder="Filtrar plato…" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
      </div>
      <p className="text-sm text-muted-foreground">
        La foto se guarda comprimida (miniatura 160px + 800px) y se muestra en la <strong>carta QR</strong> del cliente
        y en el <strong>buscador del Salón</strong>. Formatos: JPG, PNG, WebP.
      </p>

      {filtrados.length === 0 ? (
        <Card><CardContent className="py-10 text-center text-muted-foreground">
          {platos.length === 0 ? 'No hay platos vendibles en el catálogo.' : 'Sin resultados para el filtro.'}
        </CardContent></Card>
      ) : (
        <div className="grid gap-3 grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
          {filtrados.map((p) => {
            const thumb = thumbs[p.id];
            const busy = subiendo === p.id;
            return (
              <Card key={p.id} className="overflow-hidden">
                <div className="aspect-square bg-muted/40 flex items-center justify-center overflow-hidden">
                  {thumb
                    ? <img src={thumb} alt={p.nombre} className="w-full h-full object-cover" />
                    : <ImageOff className="w-8 h-8 text-muted-foreground/50" />}
                </div>
                <CardContent className="p-2 space-y-1.5">
                  <div className="text-sm font-medium leading-tight line-clamp-2" title={p.nombre}>{p.nombre}</div>
                  <div className="text-xs text-muted-foreground font-mono">{money(p.precio_base)}</div>
                  <input ref={(el) => { fileRefs.current[p.id] = el; }} type="file" accept="image/*" className="hidden"
                         onChange={(e) => { void subir(p, e.target.files?.[0]); e.target.value = ''; }} />
                  <div className="flex items-center gap-1">
                    <button className="flex-1 inline-flex items-center justify-center gap-1 rounded-md border px-2 py-1 text-xs hover:bg-muted disabled:opacity-50"
                            disabled={busy} onClick={() => fileRefs.current[p.id]?.click()}>
                      <Upload className="w-3.5 h-3.5" /> {busy ? '…' : thumb ? 'Cambiar' : 'Subir'}
                    </button>
                    {thumb && (
                      <button className="inline-flex items-center justify-center rounded-md border px-2 py-1 text-xs hover:bg-muted disabled:opacity-50"
                              disabled={busy} title="Quitar foto" onClick={() => void quitar(p)}>
                        <Trash2 className="w-3.5 h-3.5 text-red-500" />
                      </button>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
